import React, { useState, useEffect, useCallback } from 'react';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';



// ─── Helpers ────────────────────────────────────────────────────────────────
const getWeekDates = () => {
  const today = new Date();
  const day = today?.getDay();
  const monday = new Date(today);
  monday?.setDate(today?.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d?.setDate(monday?.getDate() + i);
    return d;
  });
};

const formatDate = (d) => d?.toISOString()?.split('T')?.[0];
const isToday = (dateStr) => formatDate(new Date()) === dateStr;

const CLASS_TYPE_COLORS = {
  Yoga: '#8b5cf6', HIIT: '#ef4444', Strength: '#f59e0b',
  Cardio: '#3b82f6', Pilates: '#ec4899', General: '#6b7280',
};

const STAR_COLORS = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'];

// ─── Skeleton ────────────────────────────────────────────────────────────────
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-[var(--color-muted)] rounded ${className}`} />
);

// ─── Attendance Modal ─────────────────────────────────────────────────────────
function ClassAttendanceModal({ classData, vendorId, onClose, onSaved }) {
  const [enrollments, setEnrollments] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [existingMap, setExistingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [enrRes, attRes] = await Promise.all([
          api.get(`/classes/${classData?.id}/enrollments`),
          api.get('/attendances', { params: { class_id: classData?.id } }),
        ]);
        const enrList = enrRes?.data || [];
        setEnrollments(enrList);
        const em = {};
        (attRes?.data || [])?.forEach(a => { em[a?.member_id] = a; });
        setExistingMap(em);
        const init = {};
        enrList?.forEach(e => { init[e?.member_id] = em?.[e?.member_id] ? 'present' : 'absent'; });
        setAttendance(init);
      } catch (err) { setError(err?.message); }
      finally { setLoading(false); }
    };
    load();
  }, [classData?.id]);

  const toggle = (id) => setAttendance(p => ({ ...p, [id]: p?.[id] === 'present' ? 'absent' : 'present' }));
  const markAll = () => { const a = {}; enrollments?.forEach(e => { a[e?.member_id] = 'present'; }); setAttendance(a); };
  const presentCount = Object.values(attendance)?.filter(v => v === 'present')?.length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const now = new Date()?.toISOString();
      const checkInTime = classData?.start_date && classData?.start_time
        ? new Date(`${classData?.start_date}T${classData?.start_time}`)?.toISOString() : now;
      await Promise.all(enrollments?.map(async (e) => {
        const isPresent = attendance?.[e?.member_id] === 'present';
        const existing = existingMap?.[e?.member_id];
        if (isPresent && !existing) {
          return api.post('/attendances', {
            member_id: e?.member_id, class_id: classData?.id,
            check_in_time: checkInTime, check_in_type: 'class',
            day_of_week: new Date(checkInTime)?.getDay(), hour_of_day: new Date(checkInTime)?.getHours(),
          });
        } else if (!isPresent && existing) {
          return api.delete(`/attendances/${existing?.id}`);
        }
      }));
      onSaved?.();
      onClose?.();
    } catch (err) { setError(err?.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-base font-bold text-[var(--color-foreground)]">Mark Attendance</h2>
            <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5">{classData?.name} · {classData?.start_date}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <div className="flex items-center justify-between px-5 py-3 bg-[var(--color-muted)]/30 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-green-600">{presentCount} Present</span>
            <span className="text-sm text-[var(--color-muted-foreground)]">{enrollments?.length - presentCount} Absent</span>
          </div>
          <button onClick={markAll} className="text-xs px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors">
            Mark All Present
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}
          {loading ? (
            <div className="space-y-2">{[1,2,3]?.map(i => <Skeleton key={i} className="h-14" />)}</div>
          ) : enrollments?.length === 0 ? (
            <div className="text-center py-10 text-[var(--color-muted-foreground)]">No enrolled members</div>
          ) : (
            <div className="space-y-2">
              {enrollments?.map(e => {
                const present = attendance?.[e?.member_id] === 'present';
                return (
                  <div key={e?.id} onClick={() => toggle(e?.member_id)}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${present ? 'bg-green-50 border-green-200' : 'bg-[var(--color-muted)]/40 border-[var(--color-border)]'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${present ? 'bg-green-500 text-white' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'}`}>
                        {present ? <Icon name="Check" size={14} color="white" /> : `${e?.member_name?.[0] || '?'}`}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--color-foreground)]">{e?.member_name}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{e?.phone}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-md ${present ? 'bg-green-100 text-green-700' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'}`}>
                      {present ? 'Present' : 'Absent'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)]">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving || enrollments?.length === 0}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Assigned Classes ────────────────────────────────────────────────
function AssignedClassesSection({ trainerId, vendorId }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const weekDates = getWeekDates();

  const fetchClasses = useCallback(async () => {
    if (!trainerId) return;
    setLoading(true);
    try {
      const startOfWeek = formatDate(weekDates?.[0]);
      const endOfWeek = formatDate(weekDates?.[6]);
      const { data } = await api.get('/classes', { params: { is_active: 'true' } });
      const weekClasses = (data || [])?.filter(c => c?.start_date >= startOfWeek && c?.start_date <= endOfWeek);
      setClasses(weekClasses);
    } catch (err) { setError(err?.message); }
    finally { setLoading(false); }
  }, [trainerId]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  const getClassStatus = (cls) => {
    const now = new Date();
    const start = new Date(`${cls?.start_date}T${cls?.start_time}`);
    const end = new Date(`${cls?.start_date}T${cls?.end_time}`);
    if (now < start) return 'Upcoming';
    if (now >= start && now <= end) return 'In Progress';
    return 'Completed';
  };

  const getStatusStyle = (status) => {
    if (status === 'In Progress') return 'bg-green-100 text-green-700 border-green-200';
    if (status === 'Completed') return 'bg-gray-100 text-gray-600 border-gray-200';
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  if (loading) return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1,2,3]?.map(i => <Skeleton key={i} className="h-36" />)}
      </div>
    </div>
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
            <Icon name="CalendarDays" size={16} color="var(--color-primary)" />
          </div>
          <h2 className="text-base font-bold text-[var(--color-foreground)]">This Week's Classes</h2>
        </div>
        <span className="text-xs text-[var(--color-muted-foreground)] bg-[var(--color-muted)] px-2 py-1 rounded-full">{classes?.length} classes</span>
      </div>

      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {weekDates?.map((d, i) => {
          const dateStr = formatDate(d);
          const dayClasses = classes?.filter(c => c?.start_date === dateStr);
          const today = isToday(dateStr);
          return (
            <div key={i} className={`text-center p-2 rounded-lg ${today ? 'bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20' : ''}`}>
              <p className={`text-xs font-medium ${today ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted-foreground)]'}`}>
                {d?.toLocaleDateString('en-US', { weekday: 'short' })}
              </p>
              <p className={`text-sm font-bold ${today ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'}`}>{d?.getDate()}</p>
              {dayClasses?.length > 0 && (
                <div className="flex justify-center mt-1">
                  <span className="w-4 h-4 rounded-full bg-[var(--color-primary)] text-white text-[10px] flex items-center justify-center font-bold">{dayClasses?.length}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}

      {classes?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
            <Icon name="Calendar" size={20} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm font-medium text-[var(--color-foreground)]">No classes this week</p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">You have no assigned classes for this week</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {classes?.map(cls => {
            const status = getClassStatus(cls);
            const enrolled = Number(cls?.enrolled_count || 0);
            const typeColor = CLASS_TYPE_COLORS?.[cls?.class_type] || CLASS_TYPE_COLORS?.General;
            const today = isToday(cls?.start_date);
            return (
              <div key={cls?.id} className={`border rounded-xl p-4 transition-all ${today ? 'border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5' : 'border-[var(--color-border)] bg-[var(--color-background)]'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] truncate">{cls?.name}</p>
                    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-1" style={{ backgroundColor: `${typeColor}18`, color: typeColor }}>
                      {cls?.class_type}
                    </span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ml-2 flex-shrink-0 ${getStatusStyle(status)}`}>{status}</span>
                </div>
                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
                    <Icon name="Clock" size={11} color="currentColor" />
                    {cls?.start_time?.slice(0,5)} – {cls?.end_time?.slice(0,5)}
                  </div>
                  {cls?.branch_name && (
                    <div className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
                      <Icon name="MapPin" size={11} color="currentColor" />
                      {cls?.branch_name}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)]">
                    <Icon name="Users" size={11} color="currentColor" />
                    {enrolled} / {cls?.max_capacity} enrolled
                  </div>
                </div>
                {(status === 'In Progress' || status === 'Completed') && (
                  <button onClick={() => setSelectedClass(cls)}
                    className="w-full text-xs font-medium py-1.5 px-3 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5">
                    <Icon name="ClipboardCheck" size={12} color="white" />
                    {status === 'Completed' ? 'View/Edit Attendance' : 'Start Attendance'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedClass && (
        <ClassAttendanceModal
          classData={selectedClass}
          vendorId={vendorId}
          onClose={() => setSelectedClass(null)}
          onSaved={fetchClasses}
        />
      )}
    </div>
  );
}

// ─── Section: Enrolled Members ────────────────────────────────────────────────
function EnrolledMembersSection({ trainerId, vendorId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetch = async () => {
      if (!trainerId) return;
      setLoading(true);
      try {
        const { data } = await api.get('/members', { params: { trainer_id: trainerId, limit: 2000 } });
        setMembers(data || []);
      } catch (err) { setError(err?.message); }
      finally { setLoading(false); }
    };
    fetch();
  }, [trainerId]);

  const filtered = members?.filter(m => {
    const matchSearch = `${m?.first_name} ${m?.last_name} ${m?.email}`?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || m?.member_status === statusFilter;
    return matchSearch && matchStatus;
  });

  const getStatusStyle = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-700';
    if (status === 'inactive') return 'bg-gray-100 text-gray-600';
    if (status === 'suspended') return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const getLastCheckIn = () => '—';

  const getActivePackage = (m) => m?.package_name || '—';

  if (loading) return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="space-y-3">{[1,2,3,4]?.map(i => <Skeleton key={i} className="h-14" />)}</div>
    </div>
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <Icon name="Users" size={16} color="#3b82f6" />
          </div>
          <h2 className="text-base font-bold text-[var(--color-foreground)]">Enrolled Members</h2>
        </div>
        <span className="text-xs text-[var(--color-muted-foreground)] bg-[var(--color-muted)] px-2 py-1 rounded-full">{members?.length} total</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search members..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e?.target?.value)}
          className="px-3 py-2 text-sm border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
          <option value="trial">Trial</option>
        </select>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}

      {filtered?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
            <Icon name="UserX" size={20} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm font-medium text-[var(--color-foreground)]">No members found</p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">No members assigned to you yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)]">Member</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)] hidden sm:table-cell">Status</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)] hidden md:table-cell">Package</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)] hidden lg:table-cell">Last Check-in</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--color-muted-foreground)]">Plans</th>
              </tr>
            </thead>
            <tbody>
              {filtered?.map(m => (
                <tr key={m?.id} className="border-b border-[var(--color-border)]/50 hover:bg-[var(--color-muted)]/30 transition-colors">
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                        {m?.profile_photo_url ? (
                          <img src={m?.profile_photo_url} alt={`${m?.first_name} ${m?.last_name}`} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-[var(--color-primary)]">{m?.first_name?.[0]}{m?.last_name?.[0]}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[var(--color-foreground)]">{m?.first_name} {m?.last_name}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{m?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 hidden sm:table-cell">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${getStatusStyle(m?.member_status)}`}>{m?.member_status}</span>
                  </td>
                  <td className="py-3 px-3 hidden md:table-cell">
                    <span className="text-xs text-[var(--color-foreground)]">{getActivePackage(m)}</span>
                  </td>
                  <td className="py-3 px-3 hidden lg:table-cell">
                    <span className="text-xs text-[var(--color-muted-foreground)]">{getLastCheckIn()}</span>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${m?.workout_plan_id ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)] border-[var(--color-border)]'}`}>
                        {m?.workout_plan_id ? 'Workout ✓' : 'No Workout'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${m?.diet_plan_id ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)] border-[var(--color-border)]'}`}>
                        {m?.diet_plan_id ? 'Diet ✓' : 'No Diet'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Section: Weekly Earnings ─────────────────────────────────────────────────
function WeeklyEarningsSection({ trainerId }) {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setCommissions([]);
    setLoading(false);
  }, [trainerId]);

  const now = new Date();
  const weekStart = new Date(now); weekStart?.setDate(now?.getDate() - now?.getDay());
  const lastWeekStart = new Date(weekStart); lastWeekStart?.setDate(weekStart?.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const currentWeekEarnings = commissions?.filter(c => new Date(c?.created_at) >= weekStart)?.reduce((s, c) => s + Number(c?.amount), 0);
  const lastWeekEarnings = commissions?.filter(c => new Date(c?.created_at) >= lastWeekStart && new Date(c?.created_at) < weekStart)?.reduce((s, c) => s + Number(c?.amount), 0);
  const mtdEarnings = commissions?.filter(c => new Date(c?.created_at) >= monthStart)?.reduce((s, c) => s + Number(c?.amount), 0);
  const paidAmount = commissions?.filter(c => c?.status === 'paid')?.reduce((s, c) => s + Number(c?.amount), 0);
  const pendingAmount = commissions?.filter(c => c?.status === 'pending')?.reduce((s, c) => s + Number(c?.amount), 0);

  // Build last 8 weeks bar chart data
  const weeklyChartData = Array.from({ length: 8 }, (_, i) => {
    const wEnd = new Date(weekStart); wEnd?.setDate(weekStart?.getDate() - i * 7);
    const wStart = new Date(wEnd); wStart?.setDate(wEnd?.getDate() - 7);
    const total = commissions?.filter(c => {
      const d = new Date(c?.created_at);
      return d >= wStart && d < wEnd;
    })?.reduce((s, c) => s + Number(c?.amount), 0);
    return { week: `W${8 - i}`, earnings: total };
  })?.reverse();

  if (loading) return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="grid grid-cols-3 gap-3 mb-4">{[1,2,3]?.map(i => <Skeleton key={i} className="h-20" />)}</div>
      <Skeleton className="h-48" />
    </div>
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
          <Icon name="DollarSign" size={16} color="#16a34a" />
        </div>
        <h2 className="text-base font-bold text-[var(--color-foreground)]">Weekly Earnings</h2>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {[
          { label: 'This Week', value: currentWeekEarnings, icon: 'TrendingUp', color: '#22c55e', bg: 'bg-green-50' },
          { label: 'Last Week', value: lastWeekEarnings, icon: 'Calendar', color: '#3b82f6', bg: 'bg-blue-50' },
          { label: 'Month-to-Date', value: mtdEarnings, icon: 'BarChart2', color: '#8b5cf6', bg: 'bg-purple-50' },
        ]?.map(kpi => (
          <div key={kpi?.label} className={`${kpi?.bg} rounded-xl p-4 border border-[var(--color-border)]`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon name={kpi?.icon} size={14} color={kpi?.color} />
              <span className="text-xs text-[var(--color-muted-foreground)]">{kpi?.label}</span>
            </div>
            <p className="text-xl font-bold text-[var(--color-foreground)]">${kpi?.value?.toFixed(2)}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-medium px-3 py-1.5 bg-green-100 text-green-700 border border-green-200 rounded-full">
          Paid: ${paidAmount?.toFixed(2)}
        </span>
        <span className="text-xs font-medium px-3 py-1.5 bg-yellow-100 text-yellow-700 border border-yellow-200 rounded-full">
          Pending: ${pendingAmount?.toFixed(2)}
        </span>
      </div>

      {commissions?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Icon name="DollarSign" size={24} color="var(--color-muted-foreground)" />
          <p className="text-sm text-[var(--color-muted-foreground)] mt-2">No commission records found</p>
        </div>
      ) : (
        <div>
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Earnings — Last 8 Weeks</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weeklyChartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <Tooltip formatter={(v) => [`$${v?.toFixed(2)}`, 'Earnings']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)' }} />
              <Bar dataKey="earnings" fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Section: Member Ratings ──────────────────────────────────────────────────
function MemberRatingsSection({ trainerId, vendorId }) {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setRatings([]);
    setLoading(false);
  }, [trainerId]);

  const avgRating = ratings?.length ? (ratings?.reduce((s, r) => s + r?.rating, 0) / ratings?.length) : 0;
  const distribution = [5, 4, 3, 2, 1]?.map(star => ({
    star, count: ratings?.filter(r => r?.rating === star)?.length,
    pct: ratings?.length ? Math.round((ratings?.filter(r => r?.rating === star)?.length / ratings?.length) * 100) : 0,
  }));

  const StarDisplay = ({ rating, size = 16 }) => (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5]?.map(s => (
        <Icon key={s} name="Star" size={size} color={s <= rating ? '#f59e0b' : '#d1d5db'}
          style={{ fill: s <= rating ? '#f59e0b' : 'none' }} />
      ))}
    </div>
  );

  if (loading) return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="grid grid-cols-2 gap-4 mb-4">{[1,2]?.map(i => <Skeleton key={i} className="h-32" />)}</div>
      <div className="space-y-3">{[1,2,3]?.map(i => <Skeleton key={i} className="h-16" />)}</div>
    </div>
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
          <Icon name="Star" size={16} color="#f59e0b" />
        </div>
        <h2 className="text-base font-bold text-[var(--color-foreground)]">Member Ratings</h2>
        <span className="text-xs text-[var(--color-muted-foreground)] bg-[var(--color-muted)] px-2 py-1 rounded-full ml-auto">{ratings?.length} reviews</span>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}

      {ratings?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-3">
            <Icon name="Star" size={20} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm font-medium text-[var(--color-foreground)]">No ratings yet</p>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Member ratings will appear here</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {/* Overall rating */}
            <div className="flex flex-col items-center justify-center bg-yellow-50 rounded-xl p-5 border border-yellow-100">
              <p className="text-5xl font-bold text-[var(--color-foreground)]">{avgRating?.toFixed(1)}</p>
              <StarDisplay rating={Math.round(avgRating)} size={20} />
              <p className="text-xs text-[var(--color-muted-foreground)] mt-1">{ratings?.length} total ratings</p>
            </div>
            {/* Distribution */}
            <div className="space-y-1.5">
              {distribution?.map(d => (
                <div key={d?.star} className="flex items-center gap-2">
                  <span className="text-xs text-[var(--color-muted-foreground)] w-4">{d?.star}</span>
                  <Icon name="Star" size={11} color="#f59e0b" style={{ fill: '#f59e0b' }} />
                  <div className="flex-1 h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${d?.pct}%`, backgroundColor: STAR_COLORS?.[d?.star - 1] }} />
                  </div>
                  <span className="text-xs text-[var(--color-muted-foreground)] w-6 text-right">{d?.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent reviews */}
          <div>
            <p className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Recent Reviews</p>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {ratings?.slice(0, 10)?.map(r => (
                <div key={r?.id} className="p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-lg">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        {r?.members?.first_name} {r?.members?.last_name}
                      </p>
                      <StarDisplay rating={r?.rating} size={12} />
                    </div>
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {new Date(r?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {r?.comment && <p className="text-xs text-[var(--color-muted-foreground)] mt-1 leading-relaxed">{r?.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Section: Performance Trends ──────────────────────────────────────────────
function PerformanceTrendsSection({ trainerId, vendorId }) {
  const [classes, setClasses] = useState([]);
  const [members, setMembers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      if (!trainerId) return;
      setLoading(true);
      try {
        const eightWeeksAgo = new Date(); eightWeeksAgo?.setDate(eightWeeksAgo?.getDate() - 56);
        const startDate = formatDate(eightWeeksAgo);
        const [classRes, memberRes] = await Promise.all([
          api.get('/classes', { params: { is_active: 'true' } }),
          api.get('/members', { params: { trainer_id: trainerId, limit: 2000 } }),
        ]);
        setClasses((classRes?.data || [])?.filter(c => c?.start_date >= startDate));
        setMembers(memberRes?.data || []);
        setRatings([]);
      } catch (err) { setError(err?.message); }
      finally { setLoading(false); }
    };
    fetch();
  }, [trainerId]);

  // Build weekly trend data (last 8 weeks)
  const now = new Date();
  const weekStart = new Date(now); weekStart?.setDate(now?.getDate() - now?.getDay());

  const weeklyTrend = Array.from({ length: 8 }, (_, i) => {
    const wEnd = new Date(weekStart); wEnd?.setDate(weekStart?.getDate() - i * 7 + 7);
    const wStart = new Date(weekStart); wStart?.setDate(weekStart?.getDate() - i * 7);
    const weekClasses = classes?.filter(c => {
      const d = new Date(c?.start_date);
      return d >= wStart && d < wEnd;
    });
    const totalEnrolled = weekClasses?.reduce((s, c) => s + (c?.class_enrollments?.filter(e => e?.status === 'enrolled')?.length || 0), 0);
    const totalAttended = weekClasses?.reduce((s, c) => s + (c?.attendances?.length || 0), 0);
    const attRate = totalEnrolled > 0 ? Math.round((totalAttended / totalEnrolled) * 100) : 0;
    return { week: `W${8 - i}`, attendanceRate: attRate, classCount: weekClasses?.length };
  })?.reverse();

  // Member retention donut
  const activeMembers = members?.filter(m => m?.member_status === 'active')?.length;
  const churnedMembers = members?.filter(m => m?.member_status === 'inactive' || m?.member_status === 'suspended')?.length;
  const retentionData = [
    { name: 'Active', value: activeMembers, color: '#22c55e' },
    { name: 'Churned', value: churnedMembers, color: '#ef4444' },
  ]?.filter(d => d?.value > 0);

  // Top class type by attendance
  const typeStats = {};
  classes?.forEach(c => {
    if (!typeStats?.[c?.class_type]) typeStats[c?.class_type] = { enrolled: 0, attended: 0 };
    typeStats[c?.class_type].enrolled += c?.class_enrollments?.filter(e => e?.status === 'enrolled')?.length || 0;
    typeStats[c?.class_type].attended += c?.attendances?.length || 0;
  });
  const topType = Object.entries(typeStats)
    ?.map(([type, s]) => ({ type, rate: s?.enrolled > 0 ? Math.round((s?.attended / s?.enrolled) * 100) : 0 }))
    ?.sort((a, b) => b?.rate - a?.rate)?.[0];

  const avgAttRate = weeklyTrend?.length ? Math.round(weeklyTrend?.reduce((s, w) => s + w?.attendanceRate, 0) / weeklyTrend?.length) : 0;
  const totalClassesThisMonth = classes?.filter(c => new Date(c?.start_date) >= new Date(now.getFullYear(), now.getMonth(), 1))?.length;
  const avgRating = ratings?.length ? (ratings?.reduce((s, r) => s + r?.rating, 0) / ratings?.length)?.toFixed(1) : '—';

  if (loading) return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <Skeleton className="h-6 w-48 mb-4" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">{[1,2,3,4]?.map(i => <Skeleton key={i} className="h-20" />)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">{[1,2,3]?.map(i => <Skeleton key={i} className="h-48" />)}</div>
    </div>
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
          <Icon name="TrendingUp" size={16} color="#8b5cf6" />
        </div>
        <h2 className="text-base font-bold text-[var(--color-foreground)]">Performance Trends</h2>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{error}</div>}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Classes This Month', value: totalClassesThisMonth, icon: 'CalendarCheck', color: '#3b82f6', bg: 'bg-blue-50' },
          { label: 'Avg Attendance Rate', value: `${avgAttRate}%`, icon: 'UserCheck', color: '#22c55e', bg: 'bg-green-50' },
          { label: 'Active Members', value: activeMembers, icon: 'Users', color: '#8b5cf6', bg: 'bg-purple-50' },
          { label: 'Avg Member Rating', value: avgRating, icon: 'Star', color: '#f59e0b', bg: 'bg-yellow-50' },
        ]?.map(kpi => (
          <div key={kpi?.label} className={`${kpi?.bg} rounded-xl p-4 border border-[var(--color-border)]`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon name={kpi?.icon} size={14} color={kpi?.color} />
              <span className="text-xs text-[var(--color-muted-foreground)]">{kpi?.label}</span>
            </div>
            <p className="text-xl font-bold text-[var(--color-foreground)]">{kpi?.value}</p>
          </div>
        ))}
      </div>

      {topType && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-[var(--color-muted)]/30 rounded-lg border border-[var(--color-border)]">
          <Icon name="Trophy" size={14} color="#f59e0b" />
          <span className="text-xs text-[var(--color-muted-foreground)]">Top performing class type:</span>
          <span className="text-xs font-semibold text-[var(--color-foreground)]">{topType?.type}</span>
          <span className="text-xs text-green-600 font-medium">{topType?.rate}% attendance</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attendance Rate Trend */}
        <div className="lg:col-span-2">
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Attendance Rate & Classes — Last 8 Weeks</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={weeklyTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="attendanceRate" name="Att. Rate %" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="classCount" name="Classes" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Member Retention Donut */}
        <div>
          <p className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Member Retention</p>
          {retentionData?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-44 text-center">
              <Icon name="Users" size={24} color="var(--color-muted-foreground)" />
              <p className="text-xs text-[var(--color-muted-foreground)] mt-2">No member data</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie data={retentionData} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" paddingAngle={3}>
                    {retentionData?.map((entry, i) => <Cell key={i} fill={entry?.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-card)' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-3 mt-1">
                {retentionData?.map(d => (
                  <div key={d?.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d?.color }} />
                    <span className="text-xs text-[var(--color-muted-foreground)]">{d?.name}: {d?.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TrainerDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('classes');
  const [trainerProfile, setTrainerProfile] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setProfileLoading(true);
      try {
        const { data } = await api.get('/profiles/me');
        if (data) {
          setTrainerProfile(data);
          setVendorId(data?.vendor_id || data?.id);
        }
      } catch (err) { console.error(err); }
      finally { setProfileLoading(false); }
    };
    load();
  }, []);

  const tabs = [
    { id: 'classes', label: 'Classes', icon: 'CalendarDays' },
    { id: 'members', label: 'Members', icon: 'Users' },
    { id: 'earnings', label: 'Earnings', icon: 'DollarSign' },
    { id: 'ratings', label: 'Ratings', icon: 'Star' },
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              Trainer Dashboard
            </h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
              {profileLoading ? 'Loading...' : `Welcome back, ${trainerProfile?.full_name || 'Trainer'}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded-lg">
              <div className="w-7 h-7 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center">
                <Icon name="User" size={14} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-xs font-semibold text-[var(--color-primary)] capitalize">{trainerProfile?.role || 'Trainer'}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{new Date()?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/account-settings')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-lg hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="Settings" size={13} color="currentColor" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-lg hover:bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/50 transition-colors"
            >
              <Icon name="LogOut" size={13} color="var(--color-error)" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-1 shadow-[var(--shadow-sm)] mb-6 overflow-x-auto">
          {tabs?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium font-[var(--font-caption)] transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab?.id ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)]'
              }`}>
              <Icon name={tab?.icon} size={15} color={activeTab === tab?.id ? '#fff' : 'currentColor'} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {profileLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-64" />
            <Skeleton className="h-48" />
          </div>
        ) : (
          <div>
            {activeTab === 'classes' && (
              <AssignedClassesSection trainerId={user?.id} vendorId={vendorId} />
            )}
            {activeTab === 'members' && (
              <EnrolledMembersSection trainerId={user?.id} vendorId={vendorId} />
            )}
            {activeTab === 'earnings' && (
              <WeeklyEarningsSection trainerId={user?.id} />
            )}
            {activeTab === 'ratings' && (
              <MemberRatingsSection trainerId={user?.id} vendorId={vendorId} />
            )}
            {activeTab === 'performance' && (
              <PerformanceTrendsSection trainerId={user?.id} vendorId={vendorId} />
            )}
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loggingOut && setShowLogoutModal(false)} />
            <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                  <Icon name="LogOut" size={22} color="var(--color-error)" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Log Out?</h2>
                  <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                    Are you sure you want to log out?
                  </p>
                </div>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    disabled={loggingOut}
                    className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    disabled={loggingOut}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
                  >
                    {loggingOut ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Logging out...
                      </>
                    ) : (
                      <>
                        <Icon name="LogOut" size={14} color="#fff" />
                        Log Out
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
